import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, RefreshCw, ChevronRight } from "lucide-react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

// CommandPalette is a heavy modal (~3 KB gzip) that's hidden until the
// user opens it. Lazy-load it so its bytes stay out of the initial
// bundle — first open pays a one-shot Suspense delay (~50 ms on a fast
// connection), every open after that is instant from the module cache.
const CommandPalette = lazy(() => import("../components/CommandPalette"));

// Map of URL → display name for the breadcrumb. Each page lives at a
// known route, so we resolve the human label here.
const ROUTE_LABELS: Record<string, string> = {
  "/dashboard": "Overview",
  "/datasources": "Data Sources",
  "/sessions": "Sessions",
  "/projects": "Projects",
  "/tools": "Tool Usage",
  "/compliance": "Messages",
  "/users": "Users",
  "/catalog": "Agent Catalog",
  "/agents": "Agent Activity",
  "/office": "Claude in Microsoft 365",
  "/mike": "Mike",
  "/account": "Account & Workspace",
};

function isMac(): boolean {
  if (typeof navigator === "undefined") return false;
  return navigator.platform.includes("Mac");
}

function Topbar() {
  const { pathname } = useLocation();
  const currentLabel = ROUTE_LABELS[pathname] ?? "OpenWatch";
  const queryClient = useQueryClient();
  // useIsFetching returns the count of active fetches across the cache.
  // When > 0, the refresh icon spins to give honest visual feedback.
  const fetching = useIsFetching();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // The ⌘K / Ctrl+K shortcut needs to work *before* the palette has
  // ever been opened, so the listener lives here on the always-mounted
  // Topbar rather than inside the lazy palette itself.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const shortcutLabel = isMac() ? "⌘" : "Ctrl";

  return (
    <>
      <header className="topbar sticky top-0 z-40 h-14 px-6 flex items-center gap-3 max-w-[1600px] w-full mx-auto">
        {/* Breadcrumb — derived from current route */}
        <nav className="flex items-center gap-1.5 text-[12.5px]" aria-label="Breadcrumb">
          <Link to="/dashboard" className="text-fg-muted hover:text-fg-default">
            Home
          </Link>
          <ChevronRight size={12} className="text-fg-muted" />
          <span className="text-fg-strong font-medium">{currentLabel}</span>
        </nav>

        <div className="flex-1" />

        {/* Search trigger — opens the command palette */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="btn btn-ghost gap-2.5 min-w-[240px] justify-between"
          aria-label="Open command palette"
        >
          <span className="flex items-center gap-2 text-fg-muted">
            <Search size={14} />
            Search sessions, users, tools…
          </span>
          <span className="flex gap-1">
            <kbd className="text-[10.5px] px-1 py-0.5 rounded border border-border-default text-fg-muted">
              {shortcutLabel}
            </kbd>
            <kbd className="text-[10.5px] px-1 py-0.5 rounded border border-border-default text-fg-muted">
              K
            </kbd>
          </span>
        </button>

        <button
          type="button"
          className="btn btn-ghost"
          title="Refresh data"
          aria-label="Refresh data"
          onClick={() => {
            queryClient.invalidateQueries();
          }}
        >
          <RefreshCw size={14} className={fetching > 0 ? "animate-spin" : ""} />
        </button>
      </header>

      {/* Only mount the palette once the user actually opens it.
          Combined with React.lazy() above, this means zero palette
          bytes ship in the initial chunk. */}
      {paletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </Suspense>
      )}
    </>
  );
}

export default Topbar;
